import * as t from 'io-ts';

import {ModifiersEnum} from '../../../srcCommon/inboxes/search/searchTypes';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime types.
 */

export enum SearchHintTypesEnum {
  ATTACHMENT = 'attachment',
  RECIPIENT = 'recipient',
  SEARCH_FILTERS = 'search_filters',
  SENT_BY_ME = 'sent_by_me',
  TAG = 'tag',
  UNREAD = 'unread',
}

const RSearchHintValueDirty = t.intersection([
  t.type({
    value: t.string,
  }),
  t.partial({
    modifier: makeEnumRuntimeType(ModifiersEnum),
    title: t.string,
    emoji: t.string,
  }),
]);

const RSearchHintDirty = t.readonly(
  t.intersection([
    t.type({
      // @NOTE: Adding `t.string` to be able to deploy new hint types on the BE without having to change the FE first.
      type: t.union([makeEnumRuntimeType(SearchHintTypesEnum), t.string]),
      values: t.readonlyArray(RSearchHintValueDirty),
      priority: t.number,
    }),
    t.partial({
      title: t.string,
      // If a hint is transient, it means we're not keeping the value in the search bar if it's disabled.
      isTransient: t.boolean,
    }),
  ]),
);

/*
 * Types.
 */

export interface SearchHintValue extends t.TypeOf<typeof RSearchHintValueDirty> {}
export interface SearchHintModel extends t.TypeOf<typeof RSearchHintDirty> {}

export const RSearchHint = t.clean<SearchHintModel>(RSearchHintDirty as any);

/*
 * IO methods.
 */

function parseSearchHintValue(src: any): SearchHintValue {
  return {
    modifier: src.modifier,
    value: src.value,
    title: src.title,
    emoji: src.emoji,
  };
}

export function parseSearchHint(src: any): SearchHintModel {
  return {
    type: src.type,
    values: src.values.map(parseSearchHintValue),
    title: src.title,
    isTransient: src.is_transient,
    priority: src.priority || 0,
  };
}

export function parseSearchHints(src: any): ReadonlyArray<SearchHintModel> {
  return src.map(parseSearchHint);
}

/*
 * API type.
 */

export const ApiSearchHints = makeApiType('searchHints', t.readonlyArray(RSearchHintDirty), parseSearchHints);
